import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * Color System Test Component
 * Tests all color variants to ensure proper theming
 */
export const ColorSystemTest = () => {
  return (
    <div className="p-8 space-y-8 bg-background text-foreground">
      <div>
        <h1 className="text-3xl font-bold mb-2">Color System Test</h1>
        <p className="text-muted-foreground">Testing all color variants and states</p>
      </div>
      
      {/* Button Variants Test */}
      <Card>
        <CardHeader>
          <CardTitle>Button Variants</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="default">Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="accent">Accent</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
            <Button variant="subtle">Subtle</Button>
            <Button variant="success">Success</Button>
            <Button variant="warning">Warning</Button>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Button variant="default" disabled>Disabled Default</Button>
            <Button variant="secondary" disabled>Disabled Secondary</Button>
            <Button variant="success" disabled>Disabled Success</Button>
            <Button variant="warning" disabled>Disabled Warning</Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Semantic Color Test */}
      <Card>
        <CardHeader>
          <CardTitle>Semantic Colors</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <h4 className="font-semibold">Success</h4>
              <div className="bg-success-light p-3 rounded text-success-dark">Light</div>
              <div className="bg-success-base p-3 rounded text-success-foreground">Base</div>
              <div className="bg-success-dark p-3 rounded text-success-foreground">Dark</div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold">Warning</h4>
              <div className="bg-warning-light p-3 rounded text-warning-dark">Light</div>
              <div className="bg-warning-base p-3 rounded text-warning-foreground">Base</div>
              <div className="bg-warning-dark p-3 rounded text-warning-foreground">Dark</div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold">Error</h4>
              <div className="bg-error-light p-3 rounded text-error-dark">Light</div>
              <div className="bg-error-base p-3 rounded text-error-foreground">Base</div>
              <div className="bg-error-dark p-3 rounded text-error-foreground">Dark</div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-semibold">Primary</h4>
              <div className="bg-primary-light p-3 rounded text-primary-foreground">Light</div>
              <div className="bg-primary-base p-3 rounded text-primary-foreground">Base</div>
              <div className="bg-primary-dark p-3 rounded text-primary-foreground">Dark</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Badge Test */}
      <Card>
        <CardHeader>
          <CardTitle>Badge Variants</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Badge variant="default">Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge className="bg-success text-success-foreground">Success</Badge>
            <Badge className="bg-warning text-warning-foreground">Warning</Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Interactive States Test */}
      <Card>
        <CardHeader>
          <CardTitle>Interactive States</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-semibold">Hover States (hover over elements)</h4>
            <div className="flex gap-3">
              <div className="p-3 bg-accent hover:bg-accent-dark transition-colors rounded cursor-pointer">
                Hover: Accent
              </div>
              <div className="p-3 bg-secondary hover:bg-secondary-dark transition-colors rounded cursor-pointer">
                Hover: Secondary
              </div>
              <div className="p-3 bg-muted hover:bg-muted/80 transition-colors rounded cursor-pointer">
                Hover: Muted
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Surface Colors Test */}
      <Card>
        <CardHeader>
          <CardTitle>Surface Colors</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border p-4 rounded">
              <h4 className="font-semibold text-card-foreground mb-2">Card Surface</h4>
              <p className="text-card-foreground/80">This is card content with proper contrast</p>
            </div>
            
            <div className="bg-popover border border-border p-4 rounded">
              <h4 className="font-semibold text-popover-foreground mb-2">Popover Surface</h4>
              <p className="text-popover-foreground/80">This is popover content</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Text Colors Test */}
      <Card>
        <CardHeader>
          <CardTitle>Text Hierarchy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <h1 className="text-foreground text-2xl font-bold">H1: Primary Foreground</h1>
          <h2 className="text-foreground/90 text-xl font-semibold">H2: Foreground 90%</h2>
          <p className="text-foreground/80">Body: Foreground 80%</p>
          <p className="text-muted-foreground">Muted: Secondary text</p>
          <p className="text-muted-foreground/70">Muted 70%: Subtle text</p>
        </CardContent>
      </Card>
    </div>
  );
};